import { useState, useCallback } from 'react';
import { sendChatMessage } from '../Services/AIChatService';

/**
 * Custom hook for managing AI chat state.
 * Keeps session in React state (persists across remounts, not page refresh).
 * Server-side conversation persistence is handled by the Firebase function.
 */
const useAIChat = () => {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const sendMessage = useCallback(async (question) => {
    if (!question?.trim()) return;

    // Append user message immediately
    const userMessage = {
      role: 'user',
      content: question.trim(),
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    setError(null);

    try {
      const response = await sendChatMessage(question.trim(), sessionId);

      // Save session ID from first response
      if (!sessionId && response.sessionId) {
        setSessionId(response.sessionId);
      }

      const assistantMessage = {
        role: 'assistant',
        content: response.answer,
        timestamp: new Date(),
        agentsUsed: response.agentsUsed || [],
        sources: response.sources || {},
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('useAIChat sendMessage error:', err);
      const errorMsg = err?.code === 'functions/unauthenticated'
        ? 'Please log in to use the AI assistant.'
        : err?.message?.includes('deadline-exceeded') || err?.code === 'functions/deadline-exceeded'
        ? 'The request timed out. Please try a simpler question.'
        : 'Something went wrong. Please try again.';
      setError(errorMsg);

      // Remove the user message if we failed (so they can retry)
      setMessages(prev => prev.filter(m => m !== userMessage));
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setError(null);
  }, []);

  return { messages, sendMessage, loading, error, clearChat, sessionId };
};

export default useAIChat;
